/**
 * Custom hook for stateful logic that maintains a full 7-day week
 * using week offsets and the user's detected timezone
 *
 * ex) If today is Tuesday 10/19/2021 and `weekOffset` is 0,
 *     `startDate` will be 2021-10-17 (which is a Sunday)
 *     `endDate` will be 2021-10-23 (which is a Saturday)
 */

import { useState } from 'react';
import TimezoneHelper from 'lib/timezones';
import { getWeeklyTimeframeDateOnlyStrings } from 'coaching/lib/timeIntervals';

type State = {
  timezone: string;
  weekOffset: number;
};

export default function useFullWeekOffset(timezone?: string) {
  const [state, setState] = useState<State>({
    weekOffset: 0,
    // try and ascertain the user's timezone, and default to the first value
    // in the select options if we cannot
    timezone:
      timezone ??
      TimezoneHelper.userTimezoneDefaultValue ??
      TimezoneHelper.USTimezoneSelectOptions[0].value,
  });
  const advanceWeekOffset = () => {
    setState({ ...state, weekOffset: state.weekOffset + 1 });
  };

  const regressWeekOffset = () => {
    setState({ ...state, weekOffset: state.weekOffset - 1 });
  };

  const { startDate, endDate } = getWeeklyTimeframeDateOnlyStrings(
    state.weekOffset,
    state.timezone
  );

  return {
    advanceWeekOffset,
    regressWeekOffset,
    startDate,
    endDate,
    state,
  };
}
